import React, { useRef, useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import SnackBar from 'src/components/era-package/era/SnackBar'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import pathMap from 'src/paths/pathMap'
import { generatePath } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleRight as nextIcon,
  faSpinnerThird,
  faTimes
} from '@fortawesome/pro-regular-svg-icons'
import EditTradePartner from './trade-partner-form/edit/EditTradePartner'
import AbiTradePartnerApiUrls from '../../../apiUrls/AbiTradePartnerApiUrls'

export const TradePartnerDetailModal = ({
  onClose,
  id,
  fetchData,
  rowData,
  ...props
}) => {
  const dialogRef = useRef(null)
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleClose = (...args) => {
    if (typeof onClose === 'function') onClose(...args)
  }

  const handleEdit = () => {
    handleClose()
    history.push(generatePath(pathMap.ABI_TRADE_PARTNER_EDIT_VIEW, { id }))
  }

  const handleClone = () => {
    setLoading(true)

    // BACKEND TODO: clone the item with provided `id` and generate a new ID
    fetch(AbiTradePartnerApiUrls.CLONE + `?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((json) => {
        setLoading(false)
        if (!!json.data) {
          handleClose()
          history.push(
            generatePath(pathMap.ABI_TRADE_PARTNER_EDIT_VIEW, {
              id: json.data.id
            })
          )
        }
      })
      .catch((err) => {
        setLoading(false)
        console.error(err)
      })
  }

  const handleDeleteDialog = () => {
    setOpenDeleteDialog(true)
  }

  const handleDelete = () => {
    console.log('delete..')
    setLoading(true)

    fetch(AbiTradePartnerApiUrls.DELETE + `?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((json) => {
        setLoading(false)
        if (!!json.data) {
          setOpenDeleteDialog(false)
          handleClose()
          fetchData()
        }
      })
      .catch((err) => {
        setLoading(false)
        console.error(err)
      })
  }

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  return (
    <>
      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title='Error'
        type='error'
        open={showErrorSnackbar}
        onClose={() => {
          setShowErrorSnackbar(false)
          setErrorMessage('')
        }}
        duration={8000}
      >
        {errorMessage || 'Internal error occurred, please try again later.'}
      </SnackBar>

      <Dialog
        onClose={handleClose}
        ref={dialogRef}
        PaperProps={{
          sx: {
            maxWidth: '100dvw',
            width: '100dvw',
            maxHeight: {
              xs: '100dvh',
              sm: '90vh'
            },
            m: {
              xs: 0,
              sm: 4
            }
          }
        }}
        TransitionProps={{
          onEntered: () => {
            // Comprehensive cleanup of any remaining focused elements in main wrapper
            const mainWrapper = document.querySelector('.mainwrapper')
            if (mainWrapper) {
              // Find and blur any focused elements
              const focusedElements = mainWrapper.querySelectorAll(':focus')
              focusedElements.forEach((element) => {
                element.blur()
              })

              // Extra safety: blur any MUI buttons that might be retaining focus
              const muiButtons = mainWrapper.querySelectorAll(
                '.MuiButton-root, .MuiButtonBase-root'
              )
              muiButtons.forEach((button) => {
                if (document.activeElement === button) {
                  button.blur()
                }
              })
            }

            // Ensure focus is properly set in the modal
            setTimeout(() => {
              const dialog = dialogRef.current
              if (dialog) {
                const firstFocusable = dialog.querySelector(
                  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
                if (firstFocusable) {
                  firstFocusable.focus()
                }
              }
            }, 10)
          }
        }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
        onKeyDown={handleKeyDown}
        aria-labelledby='view-trade-partner-modal-title'
        aria-describedby='view-trade-partner-modal-description'
        role='dialog'
        {...props}
      >
        <DialogTitle
          id='view-trade-partner-modal-title'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          View Trade Partner
          <IconButton
            aria-label='Close view trade partner modal'
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
            title='Close modal'
          >
            <Box
              component={FontAwesomeIcon}
              icon={faTimes}
              sx={{ width: '1em' }}
            />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          id='view-trade-partner-modal-description'
          sx={{
            px: {
              xs: 2,
              sm: 4
            }
          }}
        >
          <Box>
            <EditTradePartner readOnly id={id} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ pt: 3 }}>
          <Buttons
            advance={
              <Button
                onClick={handleClose}
                variant='contained'
                title='Close Modal'
              >
                Okay
              </Button>
            }
            other={
              <>
                <Button
                  variant='outlined'
                  onClick={handleEdit}
                  title='Edit this Trade Partner'
                >
                  Edit
                </Button>
                <Button
                  variant='outlined'
                  onClick={handleClone}
                  title='Clone a copy of this Trade Partner'
                  disabled={loading}
                  endIcon={
                    <FontAwesomeIcon
                      icon={loading ? faSpinnerThird : nextIcon}
                      width={10}
                      spin={loading}
                    />
                  }
                >
                  Clone
                </Button>
              </>
            }
          />
          <Button
            variant='outlined'
            onClick={handleDeleteDialog}
            color='error'
            title='Proceed with Caution: Delete this Trade Partner'
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Are you sure you want to delete this record?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color='error'
            disabled={loading}
            endIcon={
              <FontAwesomeIcon
                icon={loading ? faSpinnerThird : nextIcon}
                width={10}
                spin={loading}
              />
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
